// Full-document render of the Oscar thank-you (order confirmation) view:
// PageSkeleton wrapping tempurpedic's intro greeting + create-account island,
// the pillow-sweepstakes promo, the OrderPipelineConfig delivery tracker, the
// display-only offer islands, and the e-signature / biller footer -- plus the
// deferred GA4 dataLayer events and the Google Seller Ratings opt-in.
//
// The order tracker and the intro/sweepstakes/esig/offer companion content
// diverge from every shared thank-you shape, so they travel through the opaque
// `rich_order_json` / `thank_you_extras_json` seams (serialized
// TPOrderTrackerInterface / TPThankYouExtrasInterface) and are parsed here. All
// localized copy is resolved server-side (fr_CA on tempurca); the two
// blocktrans strings that carry inline markup (intro copy + sweepstakes copy)
// arrive as pre-escaped HTML and are injected so their msgids stay intact.
// The legacy .thank-you-esig chat/phone block never rendered (its
// customer_service_settings guard referenced an undefined variable), so it is
// intentionally absent.
import React from "react";
import { DataLayerPush } from "tsi-common-react/src/components/DataLayerPush/index";
import { GASeller } from "tsi-common-react/src/components/GASeller/index";

import { interfaces, templates } from "@reactivated";

import { OrderDetailTracker } from "../components/OrderDetailTracker";
import { PageSkeleton } from "../components/PageSkeleton";
import { Template as CreateAccount } from "./CreateAccountTemplate";
import { Template as OrderOffer } from "./OrderOfferTemplate";

type IExtras = interfaces.TPThankYouExtrasInterface;
type IIntro = IExtras["intro"];
type ISweepstakes = NonNullable<IExtras["sweepstakes"]>;
type IEsig = NonNullable<IExtras["esig"]>;

const Intro = (props: {
    intro: IIntro;
    createAccount: templates.ThankYouDocumentTemplate["create_account"];
}): React.JSX.Element => {
    const { intro, createAccount } = props;
    return (
        <div className="intro">
            <h1 className="intro__title">
                {intro.title_lead} <span>{intro.first_name}.</span>{" "}
                {intro.title_tail}
            </h1>
            <p
                className="intro__copy"
                dangerouslySetInnerHTML={{ __html: intro.copy_html }}
            />
            {createAccount && <CreateAccount {...createAccount} />}
        </div>
    );
};

const SweepstakesContent = (props: {
    sweepstakes: ISweepstakes;
    gridItems: boolean;
}): React.JSX.Element => {
    const { sweepstakes, gridItems } = props;
    const gi = gridItems ? " grid-item" : "";
    return (
        <>
            <h1
                className={`gtwo-module__header${gi}`}
                dangerouslySetInnerHTML={{ __html: sweepstakes.header_html }}
            />
            <div className={`gtwo-module__icon-content${gi}`}>
                <div className="gtwo-module__icon-container">
                    <img
                        className="gtwo-module__icon"
                        src={sweepstakes.fb_icon_url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                    <img
                        className="gtwo-module__icon"
                        src={sweepstakes.twitter_icon_url}
                        alt=""
                        loading="lazy"
                        decoding="async"
                    />
                </div>
            </div>
            <div
                className={`gtwo-module__copy${gi}`}
                dangerouslySetInnerHTML={{ __html: sweepstakes.copy_html }}
            />
            <div className={`gtwo-module__cta-button-section${gi}`}>
                <a
                    className="button button--inverse gtwo-module__cta-button"
                    href={sweepstakes.cta_url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {sweepstakes.cta_label}
                </a>
            </div>
        </>
    );
};

const Sweepstakes = (props: {
    sweepstakes: ISweepstakes;
}): React.JSX.Element => {
    const { sweepstakes } = props;
    if (sweepstakes.layout === "grid") {
        return (
            <div className="gtwo-module">
                <div className="gtwo-module__content">
                    <section className="gtwo-module__grid">
                        <article className="gtwo-module__grid__col">
                            <img
                                className="gtwo-module__hero-image"
                                src={sweepstakes.hero_image_url}
                                alt=""
                                loading="lazy"
                                decoding="async"
                            />
                        </article>
                        <article className="gtwo-module__grid__col">
                            <SweepstakesContent
                                sweepstakes={sweepstakes}
                                gridItems={true}
                            />
                        </article>
                    </section>
                </div>
            </div>
        );
    }
    return (
        <div className="gtwo-module">
            <div className="gtwo-module__content">
                {sweepstakes.hero_image_url && (
                    <div className="gtwo-module__image-container">
                        <img
                            className="gtwo-module__image"
                            src={sweepstakes.hero_image_url}
                            alt=""
                            loading="lazy"
                            decoding="async"
                        />
                    </div>
                )}
                <SweepstakesContent
                    sweepstakes={sweepstakes}
                    gridItems={false}
                />
            </div>
        </div>
    );
};

const Esig = (props: { esig: IEsig }): React.JSX.Element => {
    const { esig } = props;
    return (
        <div className="thank-you-esig__copy">
            <span className="thank-you-esig__copy--bold">{esig.label}</span>{" "}
            {esig.text_html}
        </div>
    );
};

export const Template = (
    props: templates.ThankYouDocumentTemplate,
): React.JSX.Element => {
    const tracker = props.rich_order_json
        ? (JSON.parse(
              props.rich_order_json,
          ) as interfaces.TPOrderTrackerInterface)
        : null;
    const extras = props.thank_you_extras_json
        ? (JSON.parse(props.thank_you_extras_json) as IExtras)
        : null;
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            {/* {% block extra_meta %}: React 19 hoists this into <head>. */}
            <meta name="chatTags" content="thankyou" />
            <div className="thank-you-container l-capped-width u-margin-bottom--xl u-full-width-title">
                <div className="thank-you">
                    {extras && (
                        <Intro
                            intro={extras.intro}
                            createAccount={props.create_account}
                        />
                    )}
                    {extras?.sweepstakes && (
                        <Sweepstakes sweepstakes={extras.sweepstakes} />
                    )}
                </div>

                {tracker && <OrderDetailTracker data={tracker} />}

                {extras?.offers.map((offer, i) => (
                    <div
                        key={i}
                        className="thank-you-order-offer l-capped-width"
                    >
                        <OrderOffer {...offer} />
                    </div>
                ))}
            </div>

            <div className="thank-you-esig">
                {extras?.esig && <Esig esig={extras.esig} />}
                {props.biller_snippet && (
                    <div
                        className="thank-you-esig__copy"
                        dangerouslySetInnerHTML={{
                            __html: props.biller_snippet,
                        }}
                    />
                )}
            </div>

            <DataLayerPush events={props.ga_events} />
            <GASeller surveyData={props.ga_seller_survey_data} />
        </PageSkeleton>
    );
};
